// According to ARIA accessibility guidelines Tooltips should close on Escape.
// https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/tooltip_role
howspace.components.toolTip = (() => {
    'use strict';

    const showTooltip = elem => {
        if (elem instanceof jQuery) {
            elem = elem[0];
        }

        // If the element is expanded, do not show the tooltip (looks nasty on top or below the expanded menu for example).
        if (elem.getAttribute('aria-expanded') === 'true') {
            return;
        }

        if (getComputedStyle(elem).getPropertyValue('--enable-tooltip') !== 'true') {
            return;
        }

        if (elem.dataset.tooltipId) {
            // Tooltip is already shown.
            return;
        }

        const tooltipId = 'howspace-tooltip-' + real.uid();
        let tooltipText = null;
        if (elem.classList.contains('disabled') || elem.hasAttribute('disabled')) {
            tooltipText = elem.dataset.disabledTitle;
        }
        if (tooltipText === null || tooltipText === undefined) {
            tooltipText = elem.dataset.title;
        }
        if (tooltipText === '' || tooltipText === undefined) {
            return;
        }
        const tooltip = document.createElement('div');
        tooltip.id = tooltipId;
        tooltip.className = 'howspace-tooltip position-absolute';
        tooltip.setAttribute('role', 'tooltip');
        tooltip.setAttribute('aria-hidden', 'true');
        tooltip.insertAdjacentHTML('afterbegin', '<div class="arrow-shadow"></div><div class="arrow"></div>');
        const inner = document.createElement('div');
        inner.className = 'tooltip-inner';
        const isHtml = elem.dataset.tooltipHtml === 'true';
        // The text is plain by default. Markup is honoured only when the element opts in, and even then
        // only the inline formatting a tooltip can hold survives, so a title built from user or model text
        // cannot carry script.
        if (isHtml) {
            inner.innerHTML = DOMPurify.sanitize(tooltipText, {
                ALLOWED_TAGS: ['span', 'br', 'strong', 'em', 'b', 'i', 'time', 'p', 'ul', 'ol', 'li'],
                ALLOWED_ATTR: ['class', 'datetime'],
            });
        } else {
            inner.textContent = tooltipText;
        }
        tooltip.append(inner);

        elem.dataset.flipVertical = 'true';
        if (!elem.dataset.placement) {
            elem.dataset.placement = 'top';
        }
        elem.dataset.tooltipId = tooltipId;
        const plainTooltipText = toPlainText(tooltipText, isHtml);
        if (!elem.hasAttribute('aria-describedby')
            && elem.getAttribute('aria-label') !== plainTooltipText
            && elem.textContent.trim() !== plainTooltipText.trim()) {
            // Only describe the element while the tooltip is open if nothing else already describes it.
            // A permanent description outranks a hover hint, and pointing at both would read the same text twice.
            // The same goes for an element already named with the tooltip text, such as a cut off text.
            elem.setAttribute('aria-describedby', tooltipId);
        }
        elem.after(tooltip);

        howspace.popover.open(elem, tooltip);
    };

    // The tooltip text without markup, for naming the element that shows it.
    const toPlainText = (text, isHtml) => {
        if (!isHtml) {
            return text;
        }
        return new DOMParser().parseFromString(text, 'text/html').body.textContent.trim();
    };

    // A container with tabindex="-1", such as a sidebar or a dialog, only takes focus from script.
    const INTERACTIVE_ANCESTOR_SELECTOR = 'a[href], button, summary, [role="button"], [role="link"], '
        + '[role="menuitem"], [role="option"], [role="tab"], [tabindex]:not([tabindex="-1"])';

    // Pointer users can move onto the tooltip, so it closes after a short delay unless the pointer gets there.
    // The tooltip itself takes no pointer events, so clicks reach what it covers. The pointer position tells
    // whether it rests on the tooltip.
    const HIDE_DELAY_MS = 150;
    const hideTimers = new Map();
    const pointer = {x: -1, y: -1};

    document.addEventListener('pointermove', e => {
        pointer.x = e.clientX;
        pointer.y = e.clientY;
    }, {capture: true, passive: true});

    // The pointer can leave the window straight from a tooltip, which then no longer holds it open.
    document.addEventListener('mouseout', e => {
        if (e.relatedTarget === null) {
            pointer.x = -1;
            pointer.y = -1;
        }
    }, {capture: true, passive: true});

    const isPointerOverTooltip = elem => {
        const rect = document.getElementById(elem.dataset.tooltipId ?? '')?.getBoundingClientRect();
        return rect !== undefined && pointer.x >= rect.left && pointer.x <= rect.right
            && pointer.y >= rect.top && pointer.y <= rect.bottom;
    };

    const cancelHide = elem => {
        clearTimeout(hideTimers.get(elem));
        hideTimers.delete(elem);
    };

    const hideTooltipSoon = elem => {
        cancelHide(elem);
        hideTimers.set(elem, setTimeout(() => {
            hideTimers.delete(elem);
            if (isPointerOverTooltip(elem)) {
                hideTooltipSoon(elem);
                return;
            }
            // A tooltip opened by keyboard focus stays until blur.
            if (!hasKeyboardFocus(elem)) {
                hideTooltip(elem);
            }
        }, HIDE_DELAY_MS));
    };

    // A mouse click also focuses, so check focus-visible. An owner inside a control is focused through the control.
    const hasKeyboardFocus = elem => {
        const focused = document.activeElement;
        return elem.matches(':focus-visible')
            || (focused?.matches(':focus-visible') === true && getAncestorFocusTooltips(focused).includes(elem));
    };

    // Owners inside a control have no tab stop of their own. Keyboard focus on the control opens their tooltips.
    // The label of a form control, such as a radio card, counts as part of the control.
    const getAncestorFocusTooltips = elem => {
        if (!(elem instanceof Element) || elem.classList.contains('tooltip')) {
            return [];
        }
        return [elem, ...(elem.labels ?? [])].flatMap(scope => {
            return [...scope.querySelectorAll('.tooltip[data-tooltip-on-ancestor-focus="true"]')];
        });
    };

    const hideTooltip = elem => {
        cancelHide(elem);
        const tooltipId = elem.dataset.tooltipId;
        if (!tooltipId) {
            return;
        }

        delete elem.dataset.tooltipId;
        document.querySelectorAll(`[aria-describedby="${tooltipId}"]`).forEach(described => {
            described.removeAttribute('aria-describedby');
        });
        const tooltip = document.getElementById(tooltipId);
        if (tooltip === null) {
            return;
        }

        howspace.popover.close(elem, tooltip);
        tooltip.remove();
    };

    // Escape closes every open tooltip, also one opened by the pointer while focus is somewhere else.
    document.addEventListener('keydown', e => {
        if (e.key !== 'Escape') {
            return;
        }
        const open = [...document.querySelectorAll('.tooltip[data-tooltip-id]')];
        if (open.some(hasKeyboardFocus)) {
            // The tooltip of the focused element takes this Escape, so the sidebar around it stays open.
            e.preventDefault();
        }
        open.forEach(hideTooltip);
    }, {capture: true});

    document.addEventListener('mouseenter', e => {
        const target = e.target;
        if (!target?.classList?.contains('tooltip')) {
            return;
        }
        pointer.x = e.clientX;
        pointer.y = e.clientY;
        cancelHide(target);
        // The pointer may rest on an open tooltip and reach the element under it. That tooltip stays the only one.
        const covered = [...document.querySelectorAll('.tooltip[data-tooltip-id]')].some(elem => {
            return elem !== target && isPointerOverTooltip(elem);
        });
        if (!covered) {
            showTooltip(target);
        }
    }, {capture: true});

    document.addEventListener('mouseleave', e => {
        const target = e.target;
        if (target?.classList?.contains('tooltip')) {
            hideTooltipSoon(target);
        }
    }, {capture: true});

    document.addEventListener('click', e => {
        const clickTooltip = e.target.closest?.('.tooltip[data-tooltip-show-on-click="true"]');
        document.querySelectorAll('.tooltip[data-tooltip-show-on-click="true"][data-tooltip-id]').forEach(elem => {
            if (elem !== clickTooltip) {
                hideTooltip(elem);
            }
        });
        if (clickTooltip) {
            // A touch-generated click may follow focus, which has already opened the tooltip.
            showTooltip(clickTooltip);
            return;
        }
        const tooltip = e.target;
        if (tooltip?.classList?.contains('tooltip')) {
            hideTooltip(tooltip);
        }
    }, {capture: true});

    document.addEventListener('focus', e => {
        const target = e.target;
        // Only keyboard focus opens a tooltip. A click, or focus a dialog returns after a click, leaves it to the pointer.
        if (!target?.matches?.(':focus-visible')) {
            return;
        }
        if (target.classList.contains('tooltip')) {
            showTooltip(target);
            return;
        }
        getAncestorFocusTooltips(target).forEach(tooltip => {
            showTooltip(tooltip);
            // A cut off text that shows its own tooltip is already part of the control's name.
            if (tooltip.dataset.tooltipId && !target.hasAttribute('aria-describedby')
                && tooltip.textContent.trim() !== (tooltip.dataset.title ?? '').trim()) {
                target.setAttribute('aria-describedby', tooltip.dataset.tooltipId);
            }
        });
    }, {capture: true});

    document.addEventListener('blur', e => {
        const target = e.target;
        if (target?.classList?.contains('tooltip')) {
            hideTooltip(target);
            return;
        }
        getAncestorFocusTooltips(target).forEach(hideTooltip);
    }, {capture: true});

    return {
        init: elem => {
            // An icon with its own tooltip can't otherwise be reached by keyboard and screen reader users. An icon
            // inside a control is left alone, the control carries the focus. Some templates already make the icon
            // focusable, and it still needs a role and a name.
            $(elem).find('i.icon.tooltip:not([tabindex="-1"])').each(function () {
                const text = toPlainText(this.dataset.title ?? '', this.dataset.tooltipHtml === 'true');
                if (text === '' || this.parentElement?.closest(INTERACTIVE_ANCESTOR_SELECTOR) !== null) {
                    return;
                }
                this.setAttribute('tabindex', '0');
                this.setAttribute('role', 'img');
                this.removeAttribute('aria-hidden');
                if (!this.hasAttribute('aria-label')) {
                    this.setAttribute('aria-label', text);
                }
            });
            // The server puts a text, badge or progress with a tooltip in the tab order without knowing its
            // container. Inside a control, such as a card's collapse button, that would nest one tab stop in another.
            const ownerSelector = '.tooltip[tabindex="0"]'
                + ':not(a, button, input, select, textarea, [role]:not([role="img"]))';
            $(elem).find(ownerSelector).each(function () {
                if (this.parentElement?.closest(INTERACTIVE_ANCESTOR_SELECTOR) === null) {
                    return;
                }
                this.removeAttribute('tabindex');
                this.dataset.tooltipOnAncestorFocus = 'true';
            });
        },
        enable: (tooltip) => {
            if (tooltip instanceof jQuery) {
                tooltip = tooltip[0];
            }
            if (!tooltip) {
                return;
            }
            tooltip.classList.add('tooltip');
            if (tooltip.dataset.originalTitle) {
                tooltip.dataset.title = tooltip.dataset.originalTitle || '';
                delete tooltip.dataset.originalTitle;
            }
        },
        disable: (tooltip) => {
            if (tooltip instanceof jQuery) {
                tooltip = tooltip[0];
            }
            if (!tooltip) {
                return;
            }
            hideTooltip(tooltip);
            tooltip.classList.remove('tooltip');
            if (tooltip.dataset.title) {
                tooltip.dataset.originalTitle = tooltip.dataset.title || '';
                delete tooltip.dataset.title;
            }
        },
        update: (tooltip, title) => {
            if (tooltip instanceof jQuery) {
                tooltip = tooltip[0];
            }
            if (!tooltip) {
                return;
            }
            tooltip.dataset.title = title;
            const tooltipId = tooltip.dataset.tooltipId;
            if (tooltipId) {
                const tooltipElem = document.getElementById(tooltipId);
                if (tooltipElem !== null) {
                    tooltipElem.querySelector('.tooltip-inner').textContent = title;
                }
            }
        },
        show: (tooltip) => {
            showTooltip(tooltip);
        },
        hide: (tooltip) => {
            hideTooltip(tooltip);
        }
    };
})();
